// ======================================================================
// File: src/modules/ppe/pages/procurement/purchase-return/form/PurchaseReturnFormDrawer.jsx
// Module: PPE → Procurement → Purchase Return
//
// Purpose:
// - Drawer component for creating Purchase Return header
// - Selects only POSTED GRNs
// - Loads Vendor and PO from selected GRN
// - Follows same drawer structure as GRNFormDrawer.jsx
// ======================================================================

import React, { useEffect, useState } from "react";

import purchaseReturnService from "../services/purchaseReturnService";
import grnService from "../../grn/services/grnService";

import "../purchase-return-ui.css";

import SearchableDropdown from "../../../../components/common/SearchableDropdown";

export default function PurchaseReturnFormDrawer({
  open,
  mode = "create",
  record,
  onClose,
  onSaved,
}) {

  const today = new Date().toISOString().split("T")[0];

  const [form, setForm] = useState({
    return_date: today,

    vendor_id: "",
    vendor_label: "",

    po_id: "",
    po_label: "",

    grn_id: "",
    grn_label: "",

    return_type: "",

    reason: "",
    remarks: "",
  });

  const [saving, setSaving] = useState(false);
  const [loadingGRN, setLoadingGRN] = useState(false);

  const isEdit = mode === "edit";

  // ----------------------------------------------------------
  // RESET / PREFILL
  // ----------------------------------------------------------
  useEffect(() => {

    if (record) {

      setForm({
        return_date: record.return_date || today,

        vendor_id: record.vendor_id || "",
        vendor_label:
          record.vendor_name ||
          record.vendor_id ||
          "",

        po_id: record.po_id || "",
        po_label:
          record.po_no ||
          record.po_id ||
          "",

        grn_id: record.grn_id || "",
        grn_label:
          record.grn_no ||
          record.grn_id ||
          "",

        return_type: record.return_type || "",

        reason: record.reason || "",
        remarks: record.remarks || "",
      });

    } else {

      setForm({
        return_date: today,

        vendor_id: "",
        vendor_label: "",

        po_id: "",
        po_label: "",

        grn_id: "",
        grn_label: "",

        return_type: "",

        reason: "",
        remarks: "",
      });
    }

  }, [record, open]);

  // ----------------------------------------------------------
  // NORMAL INPUT CHANGE
  // ----------------------------------------------------------
  const handleChange = (e) => {

    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ----------------------------------------------------------
  // LOAD SELECTED GRN
  // ----------------------------------------------------------
  const handleGRNChange = async (value) => {

    if (!value) {
      return;
    }

    try {

      setLoadingGRN(true);

      const result = await grnService.getGRNById(value.id);

      const grn = result?.data || result;

      setForm((prev) => ({
        ...prev,

        grn_id: grn.id || value.id,

        grn_label:
          grn.grn_no ||
          value.grn_no ||
          value.label ||
          value.id,

        vendor_id:
          grn.vendor_id ||
          value.vendor_id ||
          "",

        vendor_label:
          grn.vendor_name ||
          value.vendor_name ||
          value.vendor_id ||
          "",

        po_id:
          grn.po_id ||
          value.po_id ||
          "",

        po_label:
          grn.po_no ||
          value.po_no ||
          value.po_id ||
          "",
      }));

    } catch (error) {

      console.error("Error loading GRN detail:", error);

      alert(
        error?.response?.data?.message ||
        "Failed to load GRN details"
      );

    } finally {

      setLoadingGRN(false);
    }
  };

  // ----------------------------------------------------------
  // SEARCH POSTED GRNs
  // ----------------------------------------------------------
  const searchGRNs = async (search) => {

    try {

      const result = await grnService.getGRNList({
        page: 1,
        page_size: 20,
        search: search || "",
        status: "POSTED",
      });

      return result?.data || [];

    } catch (error) {

      console.error("Error searching GRNs:", error);

      return [];
    }
  };

  // ----------------------------------------------------------
  // SAVE PURCHASE RETURN HEADER
  // ----------------------------------------------------------
  const handleSave = async () => {

    try {

      // ---- BASIC VALIDATIONS ----

      if (!form.return_date) {
        alert("Please select Return Date");
        return;
      }

      if (!form.grn_id) {
        alert("Please select GRN");
        return;
      }

      if (!form.vendor_id) {
        alert("Vendor could not be determined from selected GRN");
        return;
      }

      if (!form.po_id) {
        alert("Purchase Order could not be determined from selected GRN");
        return;
      }

      if (!form.return_type) {
        alert("Please enter Return Type");
        return;
      }

      setSaving(true);

      const payload = {
        return_date: form.return_date,

        vendor_id: form.vendor_id,

        po_id: form.po_id,

        grn_id: form.grn_id,

        return_type: form.return_type,

        reason: form.reason,

        remarks: form.remarks,
      };

      await purchaseReturnService.createPurchaseReturn(payload);

      alert("Purchase Return created successfully");

      if (onSaved) {
        onSaved();
      }

      onClose();

    } catch (error) {

      alert(
        error?.response?.data?.message ||
        "Failed to create Purchase Return"
      );

    } finally {

      setSaving(false);
    }
  };

  if (!open) {
    return null;
  }

  // ----------------------------------------------------------
  // RENDER DRAWER
  // ----------------------------------------------------------
  return (
    <div className="erp-drawer-overlay">

      <div className="erp-drawer">

        {/* HEADER */}
        <div className="erp-drawer-header">

          <h5>
            {isEdit
              ? "Edit Purchase Return"
              : "Create Purchase Return"}
          </h5>

          <button
            className="btn-close"
            onClick={onClose}
          />

        </div>

        {/* BODY */}
        <div className="erp-drawer-body">

          <div className="row g-3">

            {/* RETURN DATE */}
            <div className="col-6">

              <label className="form-label">
                Return Date
              </label>

              <input
                type="date"
                className="form-control"
                name="return_date"
                value={form.return_date}
                onChange={handleChange}
              />

            </div>

            {/* GRN */}
            <div className="col-6">

              <SearchableDropdown
                label="GRN"
                value={form.grn_label}
                placeholder="Search Posted GRN..."
                fetchOptions={searchGRNs}
                getOptionLabel={(o) =>
                  o.grn_no ||
                  o.label ||
                  o.id
                }
                onChange={handleGRNChange}
              />

            </div>

            {/* VENDOR */}
            <div className="col-6">

              <label className="form-label">
                Vendor
              </label>

              <input
                type="text"
                className="form-control"
                value={form.vendor_label}
                readOnly
              />

            </div>

            {/* PURCHASE ORDER */}
            <div className="col-6">

              <label className="form-label">
                Purchase Order
              </label>

              <input
                type="text"
                className="form-control"
                value={form.po_label}
                readOnly
              />

            </div>

            {/* RETURN TYPE */}
            {/* RETURN TYPE */}
              <div className="col-6">

                <label className="form-label">
                  Return Type
                  <span className="text-danger">*</span>
                </label>

                <select
                  className="form-select"
                  name="return_type"
                  value={form.return_type}
                  onChange={handleChange}
                >
                  <option value="">
                    Select Return Type
                  </option>

                  <option value="REPLACEMENT">
                    Replacement
                  </option>

                  <option value="CREDIT">
                    Credit
                  </option>

                  <option value="REFUND">
                    Refund
                  </option>

                  <option value="OTHER">
                    Other
                  </option>

                </select>

              </div>

            {/* REASON */}
            <div className="col-6">

              <label className="form-label">
                Reason
              </label>

              <input
                type="text"
                className="form-control"
                name="reason"
                value={form.reason}
                onChange={handleChange}
                placeholder="Enter reason"
              />

            </div>

            {/* REMARKS */}
            <div className="col-12">

              <label className="form-label">
                Remarks
              </label>

              <textarea
                className="form-control"
                name="remarks"
                value={form.remarks}
                onChange={handleChange}
                rows={3}
              />

            </div>

            {loadingGRN && (
              <div className="col-12">

                <div className="text-muted">
                  Loading GRN details...
                </div>

              </div>
            )}

          </div>

        </div>

        {/* FOOTER */}
        <div className="erp-drawer-footer">

          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || loadingGRN}
          >
            {saving ? "Saving..." : "Save"}
          </button>

        </div>

      </div>

    </div>
  );
}